import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, ConflictError } from '@/api/errors';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { getWithdrawalPreview, withdraw } from '@/features/subscription/api';
import { formatCurrency, formatDate } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/subscription/withdraw` — the logged-in path for the 14-day right of
 * withdrawal (art. 6:230oa BW). The email token variant goes straight to
 * the API and never reaches this app. Access ends immediately on
 * confirmation, unlike cancelling.
 */
export function WithdrawPage() {
  const { t } = useTranslation();
  const preview = useAsync(() => getWithdrawalPreview(), []);
  const [confirmed, setConfirmed] = useState<{
    amount: number | undefined;
    currency: string | undefined;
  } | null>(null);
  const submitWithdraw = useSubmit(async () => {
    const result = await withdraw();
    setConfirmed({ amount: result.refund_amount, currency: result.refund_currency });
  });

  const errorMessage = (() => {
    if (!submitWithdraw.error) return null;
    if (submitWithdraw.error instanceof ConflictError) {
      return submitWithdraw.error.reason === 'already_withdrawn'
        ? t('subscription.withdraw.errorAlreadyWithdrawn')
        : t('subscription.withdraw.errorWindowExpired');
    }
    if (submitWithdraw.error instanceof ApiError) return submitWithdraw.error.message;
    return submitWithdraw.error.message;
  })();

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('subscription.withdraw.title')} lead={t('subscription.withdraw.lead')} />
      <Card>
        {confirmed ? (
          <Alert tone="info">
            {t('subscription.withdraw.confirmed', {
              amount:
                confirmed.amount != null && confirmed.currency
                  ? formatCurrency(confirmed.amount, confirmed.currency)
                  : '',
            })}
          </Alert>
        ) : (
          <AsyncSection state={preview} label={t('subscription.withdraw.title')}>
            {(data) => (
              <div className="flex flex-col gap-4">
                <p className="text-ink text-sm">
                  {data.refund_type === 'full'
                    ? t('subscription.withdraw.refundTypeFull')
                    : t('subscription.withdraw.refundTypeProRata')}
                </p>
                {data.refund_amount != null && data.refund_currency && (
                  <p className="text-ink text-sm">
                    {t('subscription.withdraw.refundAmount')}:{' '}
                    {formatCurrency(data.refund_amount, data.refund_currency)}
                  </p>
                )}
                {data.deadline && (
                  <p className="text-ink text-sm">
                    {t('subscription.withdraw.deadline')}: {formatDate(data.deadline)}
                  </p>
                )}
                {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
                <div>
                  <Button
                    variant="danger"
                    disabled={submitWithdraw.pending}
                    onClick={() => void submitWithdraw.submit()}
                  >
                    {t('subscription.withdraw.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </AsyncSection>
        )}
      </Card>
    </section>
  );
}
